import { concat, encodePacked, getAddress, keccak256, pad } from "viem";

import { formatTokenAmount } from "../../lib/format";

import type { Address } from "viem";

// ---------------------------------------------------------------------------
// Pool definitions (matching seed/liquidity.ts)
// ---------------------------------------------------------------------------

export const V2_POOLS = [
  { pair: "WETH/USDC", tokens: ["weth", "usdc"] as const },
  { pair: "WETH/USDT", tokens: ["weth", "usdt"] as const },
  { pair: "WETH/DAI", tokens: ["weth", "dai"] as const },
  { pair: "USDC/DAI", tokens: ["usdc", "dai"] as const },
];

export const V3_POOLS = [
  {
    pair: "WETH/USDC",
    fee: "5 bps",
    feeTier: 500,
    tokens: ["weth", "usdc"] as const,
  },
  {
    pair: "WETH/USDT",
    fee: "30 bps",
    feeTier: 3000,
    tokens: ["weth", "usdt"] as const,
  },
  {
    pair: "WETH/DAI",
    fee: "30 bps",
    feeTier: 3000,
    tokens: ["weth", "dai"] as const,
  },
  {
    pair: "USDC/DAI",
    fee: "1 bps",
    feeTier: 100,
    tokens: ["usdc", "dai"] as const,
  },
  {
    pair: "WETH/WBTC",
    fee: "30 bps",
    feeTier: 3000,
    tokens: ["weth", "wbtc"] as const,
  },
];

export const V4_POOLS = [
  {
    pair: "WETH/USDC",
    fee: "30 bps",
    feeTier: 3000,
    tickSpacing: 60,
    tokens: ["weth", "usdc"] as const,
  },
  {
    pair: "WETH/DAI",
    fee: "30 bps",
    feeTier: 3000,
    tickSpacing: 60,
    tokens: ["weth", "dai"] as const,
  },
];

// ---------------------------------------------------------------------------
// Live state types
// ---------------------------------------------------------------------------

export interface V2PoolState {
  reserve0: bigint;
  reserve1: bigint;
  pairAddress: Address;
}

export interface V3PoolState {
  sqrtPriceX96: bigint;
  tick: number;
  liquidity: bigint;
  poolAddress: Address;
}

export interface V4PoolState {
  sqrtPriceX96: bigint;
  tick: number;
  liquidity: bigint;
}

// ---------------------------------------------------------------------------
// Price helpers
// ---------------------------------------------------------------------------

export function decodeSqrtPriceX96(
  sqrtPriceX96: bigint,
  decimals0: number,
  decimals1: number,
  invert = false,
): string {
  if (sqrtPriceX96 === 0n) return "N/A";
  const Q96 = 2n ** 96n;
  const numerator = sqrtPriceX96 * sqrtPriceX96;
  const denominator = Q96 * Q96;
  const decimalAdj = 10 ** (decimals0 - decimals1);
  let price = (Number(numerator) / Number(denominator)) * decimalAdj;
  if (invert && price !== 0) price = 1 / price;
  if (price < 0.0001) return price.toExponential(2);
  if (price > 1e15) return price.toExponential(2);
  if (price > 1e3)
    return price.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return price.toFixed(4);
}

export function formatCompact(value: bigint, decimals: number): string {
  const num = Number(value) / 10 ** decimals;
  if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
  if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
  if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
  if (num < 0.0001 && num > 0) return num.toExponential(2);
  return num.toFixed(4);
}

export function getDecimals(symbol: string): number {
  const map: Record<string, number> = {
    weth: 18,
    usdc: 6,
    usdt: 6,
    dai: 18,
    wbtc: 8,
  };
  return map[symbol] ?? 18;
}

export function computePairAddress(
  factory: Address,
  tokenA: Address,
  tokenB: Address,
  initCodeHash: `0x${string}`,
): Address {
  const [token0, token1] =
    tokenA.toLowerCase() < tokenB.toLowerCase()
      ? [tokenA, tokenB]
      : [tokenB, tokenA];
  const salt = keccak256(
    encodePacked(["address", "address"], [token0, token1]),
  );
  const hash = keccak256(
    concat(["0xff", pad(factory, { size: 20 }), salt, initCodeHash]),
  );
  return getAddress("0x" + hash.slice(26));
}

export function computePoolId(
  currency0: Address,
  currency1: Address,
  fee: number,
  tickSpacing: number,
  hooks: Address,
): `0x${string}` {
  return keccak256(
    encodePacked(
      ["address", "address", "uint24", "int24", "address"],
      [currency0, currency1, fee, tickSpacing, hooks],
    ),
  );
}

export { formatTokenAmount };

// ---------------------------------------------------------------------------
// TVL helpers
// ---------------------------------------------------------------------------

export function computeTvlUsd(
  reserves: { symbol: string; amount: number }[],
  prices: Record<string, number>,
): number {
  return reserves.reduce(
    (sum, r) => sum + r.amount * (prices[r.symbol.toUpperCase()] ?? 0),
    0,
  );
}

export type ReadContractFn = (
  addr: Address,
  abi: readonly unknown[],
  fn: string,
  args?: unknown[],
) => Promise<unknown>;
